import KoobLoader from './KoobLoader';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type ReactQuill from 'react-quill-new';
import KoobQuill from './KoobQuill';
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
  normalizeRichTextValue,
  pickImageFile,
  readFileAsDataUrl,
} from './richText';

const TOOLBAR = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

const IMAGE_TOOLBAR = [...TOOLBAR, ['image']];

const FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'link',
  'image',
];

type ImageStatus = 'uploading' | 'unsupported' | 'too-large' | 'failed';

export interface KoobRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  disabled?: boolean;
  hasError?: boolean;
  /** Adds the image button to the toolbar and accepts pasted/dropped images. */
  allowImages?: boolean;
  /**
   * Uploads a picked image and resolves with its URL. Without it the image is
   * inlined as a base64 data URI, which bloats every response carrying the
   * stored value — always pass an uploader for persisted content.
   */
  onUploadImage?: (file: File) => Promise<string>;
  onUploadImageError?: (error: unknown) => void;
  uploadingLabel?: string;
  unsupportedImageLabel?: string;
  imageTooLargeLabel?: string;
  imageUploadFailedLabel?: string;
  'data-testid'?: string;
}

const KoobRichTextEditor: React.FC<KoobRichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  minHeight = '160px',
  maxHeight = '320px',
  disabled = false,
  hasError = false,
  allowImages = false,
  onUploadImage,
  onUploadImageError,
  uploadingLabel = 'Uploading image...',
  unsupportedImageLabel = 'Only JPG, PNG, GIF and WebP images are supported.',
  imageTooLargeLabel = 'The image is too large (5 MB max).',
  imageUploadFailedLabel = 'The image could not be uploaded.',
  'data-testid': dataCy = 'koob-rich-text-editor',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const [imageStatus, setImageStatus] = useState<ImageStatus | null>(null);

  // Quill reads the computed display of each element to tell blocks from
  // inline text. Mounted in a portal that is not in the page yet (Headless UI
  // modals), it sees none and flattens paragraphs and lists into one line.
  // Once the editor is on the page, read the mounted value again, silently so
  // the form does not turn dirty.
  const mountedValueRef = useRef(value);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const editor = quillRef.current?.getEditor();
      const html = mountedValueRef.current;
      if (editor && html && editor.root.isConnected) {
        editor.setContents(editor.clipboard.convert({ html }), 'silent');
      }
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Quill rebuilds its toolbar whenever `modules` changes identity, which
  // would drop the selection on every parent render. The handlers therefore
  // read their callbacks from a ref instead of closing over them.
  const callbacksRef = useRef({ onUploadImage, onUploadImageError });
  callbacksRef.current = { onUploadImage, onUploadImageError };

  const insertImage = useCallback(async (file: File, at?: number) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      return;
    }

    const { onUploadImage: upload, onUploadImageError: onError } =
      callbacksRef.current;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageStatus('unsupported');
      onError?.(new Error(`Unsupported image type: ${file.type}`));
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setImageStatus('too-large');
      onError?.(new Error(`Image is larger than ${MAX_IMAGE_BYTES} bytes`));
      return;
    }

    setImageStatus('uploading');
    try {
      const url = upload ? await upload(file) : await readFileAsDataUrl(file);
      const index = at ?? editor.getSelection()?.index ?? editor.getLength();
      editor.insertEmbed(index, 'image', url, 'user');
      editor.setSelection(index + 1, 0);
      setImageStatus(null);
    } catch (error) {
      setImageStatus('failed');
      onError?.(error);
    }
  }, []);

  const handleImageButton = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      return;
    }

    // The caret is read now, not in the change handler: opening the file
    // dialog blurs the editor and getSelection() then answers null.
    const at = editor.getSelection(true)?.index ?? editor.getLength();

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPTED_IMAGE_TYPES.join(',');
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        void insertImage(file, at);
      }
    };
    input.click();
  }, [insertImage]);

  // Pasted and dropped images go through the uploader too, otherwise the
  // browser inlines them as base64 behind our back. Both listeners sit on the
  // wrapper in the capture phase, so they run before Quill's own handlers on
  // the editor root and can take the event over.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !allowImages || disabled) {
      return undefined;
    }

    const takeOver = (event: Event, file: File) => {
      event.preventDefault();
      event.stopPropagation();
      void insertImage(file);
    };

    const handlePaste = (event: ClipboardEvent) => {
      const file = pickImageFile(event.clipboardData?.files);
      if (file) {
        takeOver(event, file);
      }
    };

    const handleDrop = (event: DragEvent) => {
      const file = pickImageFile(event.dataTransfer?.files);
      if (file) {
        takeOver(event, file);
      }
    };

    wrapper.addEventListener('paste', handlePaste, true);
    wrapper.addEventListener('drop', handleDrop, true);

    return () => {
      wrapper.removeEventListener('paste', handlePaste, true);
      wrapper.removeEventListener('drop', handleDrop, true);
    };
  }, [allowImages, disabled, insertImage]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: allowImages ? IMAGE_TOOLBAR : TOOLBAR,
        handlers: { image: handleImageButton },
      },
      clipboard: { matchVisual: false },
    }),
    [allowImages, handleImageButton],
  );

  const handleChange = (content: string, _delta: any, source: string) => {
    // Quill also fires on the 'api' changes it makes while normalizing the
    // incoming value; forwarding those would mark the form dirty on open.
    if (source !== 'user') {
      return;
    }

    onChange(normalizeRichTextValue(content));
  };

  const statusLabels: Record<ImageStatus, string> = {
    uploading: uploadingLabel,
    unsupported: unsupportedImageLabel,
    'too-large': imageTooLargeLabel,
    failed: imageUploadFailedLabel,
  };

  return (
    <div
      ref={wrapperRef}
      className={`
        rounded-[10px] transition-shadow focus-within:ring-4 focus-within:ring-navy-100
        [&_.ql-toolbar]:rounded-t-[10px] [&_.ql-toolbar]:border-[1.5px] [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-navy-200 [&_.ql-toolbar]:bg-white
        [&_.ql-container]:rounded-b-[10px] [&_.ql-container]:border-[1.5px] [&_.ql-container]:border-t-0 [&_.ql-container]:border-navy-200 [&_.ql-container]:bg-white [&_.ql-container]:text-xs
        [&_.ql-editor]:min-h-[var(--koob-rich-text-min-h)] [&_.ql-editor]:max-h-[var(--koob-rich-text-max-h)]
        [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:leading-relaxed [&_.ql-editor]:outline-none
        [&:hover_.ql-container]:border-navy-300 [&:hover_.ql-toolbar]:border-navy-300 [&:focus-within_.ql-container]:border-k-h [&:focus-within_.ql-toolbar]:border-k-h
        [&_.ql-editor_p]:block
        [&_.ql-editor_h2]:text-base [&_.ql-editor_h2]:font-bold [&_.ql-editor_h2]:my-2
        [&_.ql-editor_h3]:text-sm [&_.ql-editor_h3]:font-bold [&_.ql-editor_h3]:my-1.5
        [&_.ql-editor_strong]:font-bold [&_.ql-editor_b]:font-bold
        [&_.ql-editor_em]:italic [&_.ql-editor_i]:italic
        [&_.ql-editor_u]:underline
        [&_.ql-editor_a]:text-[#F67008] [&_.ql-editor_a]:underline
        [&_.ql-editor_img]:max-w-full
        ${hasError ? '[&_.ql-toolbar]:border-red-400 [&_.ql-container]:border-red-400' : ''}
      `}
      style={
        {
          '--koob-rich-text-min-h': minHeight,
          '--koob-rich-text-max-h': maxHeight,
        } as React.CSSProperties
      }
      data-testid={dataCy}
    >
      <KoobQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={handleChange}
        onBlur={onBlur}
        readOnly={disabled}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />

      {imageStatus && (
        <p
          className={`mt-1 mb-0 flex items-center gap-2 text-[11px] ${
            imageStatus === 'uploading' ? 'text-slate-500' : 'text-red-500'
          }`}
          data-testid={`${dataCy}-image-status`}
        >
          {imageStatus === 'uploading' && (
            <KoobLoader size={16} color="currentColor" />
          )}
          {statusLabels[imageStatus]}
        </p>
      )}
    </div>
  );
};

export default KoobRichTextEditor;
